import React, { FunctionComponent, useCallback, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import { useTranslate } from "src/locales";
import ChoicesCodeUpdate from "./choiescode-update";

// NOTE: Bu component, kullanıcı tarafından paylaşılan Figma/CSS taslağına göre,
// Seçtikleriniz panelinin sağ tarafındaki %35 alanı temsil eder.
const SelectedStockRightPanel: FunctionComponent = () => {
  const { t } = useTranslate();
  const [activeTab, setActiveTab] = useState<"serial" | "lot">("serial");
  const [isHgirisOpen, setHgirisOpen] = useState(false);
  const [isEditOpen, setEditOpen] = useState(false);

  const openHgiris = useCallback(() => setHgirisOpen(true), []);
  const closeHgiris = useCallback(() => setHgirisOpen(false), []);
  const openEdit = useCallback(() => setEditOpen(true), []);
  const closeEdit = useCallback(() => setEditOpen(false), []);

  return (
    <Box
      sx={{
        width: "100%",
        height: 196,
        borderRadius: "10px",
        border: "1px solid #959CA9",
        boxSizing: "border-box",
        p: 1.5,
        fontFamily: "Poppins",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        bgcolor: "#fff",
      }}
    >
      {/* Tabs */}
      <Box sx={{ display: "flex", borderBottom: "1px solid #000" }}>
        <Box
          onClick={() => setActiveTab("serial")}
          sx={{
            px: 2,
            py: 1,
            cursor: "pointer",
            color: activeTab === "serial" ? "#2196F3" : "rgba(0,0,0,0.6)",
            position: "relative",
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 500, letterSpacing: "0.4px", lineHeight: "24px" }}>
            {t("menus.stock-selected-right-tab-serial") || "Seri Barkod"}
          </Typography>
          {activeTab === "serial" && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: -1,
                height: 2,
                bgcolor: "#2196F3",
              }}
            />
          )}
        </Box>

        <Box
          onClick={() => setActiveTab("lot")}
          sx={{
            px: 2,
            py: 1,
            cursor: "pointer",
            color: activeTab === "lot" ? "#2196F3" : "rgba(0,0,0,0.6)",
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 500, letterSpacing: "0.4px", lineHeight: "24px" }}>
            {t("menus.stock-selected-right-tab-lot") || "Lot ve Skt"}
          </Typography>
        </Box>
      </Box>

      {/* Inputs row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <ByzMuiTextField
            size="small"
            fullWidth
            label={t("menus.stock-selected-right-serial-no") || "Seri No"}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <ByzMuiTextField
            size="small"
            fullWidth
            label={t("menus.stock-selected-right-component-no") || "Bileşen No"}
          />
        </Box>
        <Button
          onClick={openHgiris}
          variant="outlined"
          sx={{
            height: 26,
            minWidth: 52,
            borderRadius: "4px",
            bgcolor: "rgba(99, 180, 255, 0.2)",
            borderColor: "#2979C3",
            color: "#2979C3",
            px: 1,
            fontSize: 13,
            textTransform: "none",
            "&:hover": { bgcolor: "rgba(99, 180, 255, 0.25)", borderColor: "#2979C3" },
          }}
        >
          {t("menus.stock-selected-right-hgiris") || "H.Giriş"}
        </Button>
      </Box>

      {/* Small table (visual-only) */}
      <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            height: 39,
            display: "flex",
            alignItems: "center",
            borderBottom: "1.5px solid rgba(0,0,0,0.55)",
            fontSize: 10,
          }}
        >
          {[
            t("menus.stock-selected-right-col-ubark") || "Ü.Bark.",
            t("menus.stock-selected-right-col-ubb") || "UBB",
            t("menus.stock-selected-right-col-lot") || "Lot No",
            t("menus.stock-selected-right-col-skt") || "SKT",
            t("menus.stock-selected-right-col-amount") || "Miktar",
            t("menus.stock-selected-right-col-edit") || "Düzenle",
          ].map((h, idx) => (
            <Box key={idx} sx={{ px: 1.25, width: idx === 1 ? 70 : idx === 2 ? 55 : idx === 3 ? 70 : 60 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.17px", lineHeight: "24px" }}>
                {h}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            height: 36,
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid rgba(0,0,0,0.12)",
            fontSize: 9,
          }}
        >
          <Box sx={{ px: 1.25, width: 60 }}>
            <Typography sx={{ fontSize: 9, lineHeight: "24px" }}>855487454</Typography>
          </Box>
          <Box sx={{ px: 1.25, width: 70 }}>
            <Typography sx={{ fontSize: 9, lineHeight: "24px" }}> </Typography>
          </Box>
          <Box sx={{ px: 1.25, width: 55 }}>
            <Typography sx={{ fontSize: 9, lineHeight: "24px" }}> </Typography>
          </Box>
          <Box sx={{ px: 1.25, width: 70 }}>
            <Typography sx={{ fontSize: 9, lineHeight: "24px" }}></Typography>
          </Box>
          <Box sx={{ px: 1.25, width: 60 }}>
            <Typography sx={{ fontSize: 9, lineHeight: "24px" }}>1</Typography>
          </Box>
          <Box sx={{ px: 1.25, width: 60, display: "flex", justifyContent: "flex-start" }}>
            <ByzGrdEditButton onClick={openEdit} />
          </Box>
        </Box>
      </Box>

      {/* Dialogs */}
      <Dialog open={isHgirisOpen} onClose={closeHgiris} maxWidth="sm" fullWidth>
        <DialogTitle>{t("menus.stock-selected-right-hgiris") || "H.Giriş"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("menus.stock-selected-right-modal-placeholder") || "Demo içerik (tasarım kopyası)."}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={closeHgiris}>{t("common.close") || "Kapat"}</Button>
          </Box>
        </DialogContent>
      </Dialog>

      <ChoicesCodeUpdate
        open={isEditOpen}
        onClose={closeEdit}
        data={{ name: "Parol 500 mg Tablet", ubbCode: "", sktDate: "", lotNo: "" }}
        onSave={(updated) => {
          console.log("Updated code details:", updated);
          closeEdit();
        }}
      />
    </Box>
  );
};

export default SelectedStockRightPanel;


